/**
 * `/studio/tasks` — `mockups/studio/tasks.html`.
 *
 * Hero, then Search / facets / working views / chips, then list or board.
 * Same facets on both views. Default view is the **list**.
 */

import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { boardScrolls } from '../../../components/portal/Board.js'
import { Button, Seg, SegItem } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { isApiProblem, messageOf } from '../../../api/problem.js'
import { Notice } from '../../../components/portal/Status.js'
import { Linkish, Ref } from '../../../components/portal/Text.js'
import { usePrincipal } from '../../../auth/principal.js'
import { now } from '../../../integrations/clock.js'
import { rememberSearch, TASKS_SEARCH_KEY } from '../../../search/lastSearch.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { shortcutsApply } from '../../../shell/shortcuts.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { BoardHero } from './BoardHero.js'
import { BulkBar } from './BulkBar.js'
import { QuickCreate } from './QuickCreate.js'
import { TaskBoard } from './TaskBoard.js'
import {
  BLOCKED_ALL,
  CATEGORY_ALL,
  CLIENT_ALL,
  PRIORITY_ALL,
  QUEUE_ALL,
  SEARCH_EMPTY,
  STATUS_ALL,
  TaskFilters,
  WHO_ALL,
} from './TaskFilters.js'
import { TaskList } from './TaskList.js'
import { TaskDock, TaskNoticeProvider } from './taskNotice.js'
import {
  LIST_FACET_DEFAULTS,
  WORKING_VIEWS,
  createDefaultsFromFacets,
  facetsForWorkingView,
  listInput,
  nextSort,
  rememberScrollY,
  rememberView,
  takeScrollY,
  useTaskPages,
  useTasksPage,
  viewFor,
  type ListFacets,
  type WorkingViewId,
} from './board.js'

/** View, facets and list sort — all URL state. */
export const TasksSearchSchema = z.object({
  view: z.enum(['board', 'list']).optional(),
  client: z.string().default(CLIENT_ALL),
  who: z.string().default(WHO_ALL),
  category: z.string().default(CATEGORY_ALL),
  when: z.enum(['all', 'overdue', 'week', 'undated']).default('all'),
  blocked: z.enum(['all', 'yes', 'no']).default(BLOCKED_ALL),
  q: z.string().default(SEARCH_EMPTY),
  /*
   * A number in the address — `?priority=1` — and every earlier spelling of it.
   *
   * The facet is `ListPriority`, which is a number on purpose: the router's default
   * `stringifySearch` quotes a string that would parse back as JSON, so the digit as
   * a string went out as `?priority=%221%22`. Coercion stays because the query
   * parser hands back all three: the number `1`, the string `'1'` from a
   * hand-written link, and the string `'1'` again from one of the quoted links this
   * page has already handed out. Anything a reader typed that is not one of the five
   * is refused by key rather than by page (`parseOrDefault`).
   */
  priority: z.preprocess(
    (value) => {
      if (typeof value === 'number') return value
      if (typeof value === 'string' || typeof value === 'boolean') {
        const text = String(value)
        if (text === PRIORITY_ALL) return PRIORITY_ALL
        const digits = Number(text)
        return text.trim() !== '' && Number.isInteger(digits) ? digits : text
      }
      return undefined
    },
    z
      .union([
        z.literal(PRIORITY_ALL),
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
      ])
      .default(PRIORITY_ALL),
  ),
  status: z
    .enum(['all', 'open', 'claimed', 'in_progress', 'blocked', 'review', 'done'])
    .default(STATUS_ALL),
  queue: z.string().default(QUEUE_ALL),
  sort: z.enum(['client', 'who', 'due']).default('due'),
  dir: z.enum(['asc', 'desc']).default('asc'),
})

/** Same entry, new filters — `setFacet`'s note says why every one of these is this. */
const HERE = { replace: true } as const

export function TasksPage() {
  const [search, setSearch] = useSearchState(TasksSearchSchema)
  const principal = usePrincipal()
  const me = principal.kind === 'staff_user' ? { id: principal.userId, name: principal.name } : null
  const at = now()
  const [raised, setRaised] = useState<string | null>(null)
  /*
   * The New Task window is the **page's** state (#51). It was `QuickCreate`'s own,
   * and the `n` shortcut therefore had to walk every button on the document looking
   * for the words "New Task" to press — a shortcut reaching through the rendered
   * page for a label, which breaks the day the label changes and cannot say whether
   * it worked.
   */
  const [creating, setCreating] = useState(false)
  /*
   * Which rows are ticked.
   *
   * The page's, not the table's, because the bar it feeds is docked at the bottom of
   * the window rather than drawn above the rows (`taskNotice.tsx` `TaskDock`): at
   * forty-eight rows the count and every bulk action were a screen and a half above
   * the row somebody had just ticked.
   */
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())

  const facets: ListFacets = {
    client: search.client,
    who: search.who,
    category: search.category,
    when: search.when,
    blocked: search.blocked,
    q: search.q,
    priority: search.priority,
    status: search.status,
    queue: search.queue,
  }
  /*
   * The server filters, sorts, pages and counts (session 42): `data` is the figures,
   * the columns and the menus, asked with the facets as one input; `list` is the
   * list's rows, fifty at a time in the order the heads name. The board's columns
   * page their own (`TaskBoard`), so the list's read runs only while it is showing.
   */
  const data = useTasksPage(facets, me?.id ?? null)
  const view = viewFor(search.view, data.columns.length)
  const listAsked = listInput(data.filters, search.sort, search.dir)
  const list = useTaskPages(listAsked, view === 'list')
  const clientOf = data.clientName

  useEffect(() => {
    if (search.view !== undefined) rememberView(search.view)
  }, [search.view])

  /*
   * Scroll position (#23): filters/sort/view already live in the URL. The window
   * scroll does not — and TanStack's restoration often runs before the list has
   * height. Save on leave; restore once the board data is in.
   */
  useEffect(() => {
    /*
     * Where the reader **was**, kept as they scroll.
     *
     * Read at unmount, `window.scrollY` is 0 as often as not: the router scrolls the
     * new page to the top before this one comes apart, so what was saved was the
     * scroll of the task page being opened, and "restore" put the list back at the
     * top. The last position a scroll event saw is the one that is true.
     */
    let last = window.scrollY
    const onScroll = (): void => {
      last = window.scrollY
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      rememberScrollY(last)
    }
  }, [])

  const rowsPending = data.isPending || (view === 'list' && list.isPending)
  useEffect(() => {
    if (rowsPending) return
    const y = takeScrollY()
    if (y === null) return
    /*
     * After the rows are laid out. The effect runs on the commit that *starts* the
     * list rendering, and a page that is still 800px tall cannot be scrolled to
     * 1,500 — the browser clamps it and the position is lost for good.
     */
    const frame = requestAnimationFrame(() => {
      window.scrollTo(0, y)
    })
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [rowsPending])

  /*
   * A new question clears the ticks (session 42): a filter, the search or the sort
   * changed, so the rows are another set from its first page, and a selection made
   * of the old one would act on tasks nobody is looking at any more. Load more is
   * the same question, so it keeps them, and the rows it brings arrive unticked.
   *
   * **So does leaving the list.** A tick is a fact about the list — the board draws
   * no checkbox — but the view is not part of the question, the list's read keeps
   * its cached rows while it is switched off, and the bulk bar is docked under both
   * views. So three rows ticked and then Board left "3 selected" and every bulk
   * action live over cards with no tick on them, and nothing to untick. The view
   * is a dependency for that reason, and coming back to the list starts unticked.
   */
  const question = JSON.stringify(listAsked)
  useEffect(() => {
    setSelected((prev) => (prev.size === 0 ? prev : new Set()))
  }, [question, view])

  /*
   * A ticked row that is no longer loaded is not ticked any more: after a write the
   * pages are read again, and a task claimed or finished may have left the filter.
   * It is the fingerprint of *which* tasks are on screen, so the effect runs when
   * that changes and not on every render.
   */
  const shownRefs = list.rows.map((task) => task.ref).join('|')
  useEffect(() => {
    const here = new Set(shownRefs === '' ? [] : shownRefs.split('|'))
    setSelected((prev) => {
      const next = new Set([...prev].filter((ref) => here.has(ref)))
      return next.size === prev.size ? prev : next
    })
  }, [shownRefs])

  /* Where the list is, for the links that come back to it (`search/lastSearch.ts`). */
  useEffect(() => {
    rememberSearch(TASKS_SEARCH_KEY, search)
  }, [search])

  /**
   * Every facet change **replaces** the address rather than pushing a new one.
   *
   * Typing already did (five keystrokes, five entries); choosing did not, and a few
   * minutes of triage left twenty-five entries in the history. Back then walked the
   * filters one at a time instead of leaving the page, which is not what a reader
   * means by Back — and the one thing the pushes were for, "returning from a task
   * restores the previous filters" (#16), is unaffected: the entry the task page was
   * opened from carries the filters that were on at that moment, whether they were
   * pushed onto it or replaced into it.
   */
  const setFacet = (patch: Partial<ListFacets>): void => {
    setSearch(patch, { replace: true })
  }

  const applyWorkingView = (id: WorkingViewId): void => {
    const viewDef = WORKING_VIEWS.find((one) => one.id === id)
    if (viewDef === undefined) return
    setSearch(facetsForWorkingView(viewDef, me?.id ?? null), { replace: true })
  }

  /*
   * Everything the chips can show, and the two they cannot: a reader who presses
   * Clear all means "give me the board back", and a column still sorted by Who from
   * ten minutes ago is part of what they are clearing.
   */
  const clearAll = (): void => {
    setSearch({ ...LIST_FACET_DEFAULTS, sort: 'due', dir: 'asc' }, { replace: true })
  }

  const [shortcutHelp, setShortcutHelp] = useState(false)

  /*
   * Optional shortcuts (#51). Visible controls stay primary; these never fire while
   * typing in an input/textarea/select or with a modifier key held.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!shortcutsApply(event)) return
      const key = event.key
      if (key === '/' || key === 'n' || key === 'N') {
        event.preventDefault()
        if (key === '/') {
          document.querySelector<HTMLInputElement>('[data-tasks-search]')?.focus()
        } else {
          setCreating(true)
        }
        return
      }
      if (key === 'b' || key === 'B') {
        event.preventDefault()
        setSearch({ view: 'board' })
        rememberView('board')
        return
      }
      if (key === 'l' || key === 'L') {
        event.preventDefault()
        setSearch({ view: 'list' })
        rememberView('list')
        return
      }
      if (key === '?') {
        event.preventDefault()
        setShortcutHelp((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [setSearch])

  return (
    <TaskNoticeProvider>
      <PageTitle
        eyebrow="Studio"
        title="Tasks"
        sub="Same filters on list and board — search, facets, working views"
        actions={
          <>
            <Seg label="View">
              {(['board', 'list'] as const).map((one) => (
                <SegItem
                  key={one}
                  on={view === one}
                  onClick={() => {
                    setSearch({ view: one })
                    rememberView(one)
                  }}
                >
                  {one === 'board' ? 'Board' : 'List'}
                </SegItem>
              ))}
            </Seg>

            <QuickCreate
              defaults={createDefaultsFromFacets(facets, me?.id ?? null)}
              meName={me?.name ?? null}
              clientName={clientOf}
              open={creating}
              onOpenChange={setCreating}
              onDone={(ref) => {
                setRaised(ref)
              }}
            />
          </>
        }
      />

      {raised === null ? null : (
        <Notice role="status">
          <Ref value={raised} /> is on the board.{' '}
          <Linkish asChild>
            <Link to="/studio/tasks/$ref" params={{ ref: raised }}>
              Open it
            </Link>
          </Linkish>
        </Notice>
      )}

      {shortcutHelp ? (
        <Notice role="status">
          Shortcuts: <kbd>/</kbd> search · <kbd>n</kbd> new task · <kbd>b</kbd>/<kbd>l</kbd> board
          or list · <kbd>?</kbd> hide this. Buttons stay the main way.
        </Notice>
      ) : null}

      {data.summary === undefined ? (
        data.refused === null ? (
          <Empty>Reading the board…</Empty>
        ) : (
          /*
           * No figures at all. A 4xx is the question the address asks (a client slug the
           * server has no client for, from an old link): said once, here, with the
           * filters still below to change it. Anything else is the server failing, and
           * says so, with the way to ask again.
           */
          <Notice role="alert">
            {isApiProblem(data.refused) &&
            data.refused.status >= 400 &&
            data.refused.status < 500 ? (
              <>
                These filters could not be read. {messageOf(data.refused)} Change them, or clear
                them all.
              </>
            ) : (
              <>
                The board could not be read. {messageOf(data.refused)}{' '}
                <Button size="sm" onClick={data.retry}>
                  Try again
                </Button>
              </>
            )}
          </Notice>
        )
      ) : (
        <>
          {/* Figures from before a write that could not be read again: kept, and said. */}
          {data.refreshFailed === null ? null : (
            <Notice role="status">
              The figures could not be read again. {messageOf(data.refreshFailed)}{' '}
              <Button size="sm" onClick={data.retry}>
                Try again
              </Button>
            </Notice>
          )}
          <BoardHero
            summary={data.summary}
            accounts={data.whole?.clients ?? data.summary.clients}
            reviewCount={data.reviewCount}
            columns={data.columns.length}
            scrolls={view === 'board' && boardScrolls(data.columns.length)}
            view={view}
            at={at}
            /*
             * A figure shows **only** its own set, which is what each caption promises
             * ("Press the number to show only those"). They used to stack: Overdue
             * then Blocked then Review asked for tasks that were all three at once and
             * drew an empty page, and Open — the one that says it clears — left
             * `status=review` standing. So each one writes the other two back to their
             * defaults, and Open writes all three.
             */
            onFilter={{
              overdue: () => {
                setSearch({ when: 'overdue', blocked: BLOCKED_ALL, status: STATUS_ALL }, HERE)
              },
              blocked: () => {
                setSearch({ blocked: 'yes', when: 'all', status: STATUS_ALL }, HERE)
              },
              open: () => {
                setSearch({ when: 'all', blocked: BLOCKED_ALL, status: STATUS_ALL }, HERE)
              },
              week: () => {
                setSearch({ when: 'week', blocked: BLOCKED_ALL, status: STATUS_ALL }, HERE)
              },
              review: () => {
                setSearch({ status: 'review', when: 'all', blocked: BLOCKED_ALL }, HERE)
              },
            }}
          />
        </>
      )}

      {data.isPending ? null : (
        <>
          <TaskFilters
            facets={facets}
            onFacet={setFacet}
            onWorkingView={applyWorkingView}
            onClearAll={clearAll}
            clients={data.whole?.clients ?? []}
            assignees={data.assignees}
            services={data.services}
            me={me}
          />

          {data.refused !== null ? null : view === 'board' ? (
            data.isStale ? (
              /*
               * The columns are still the last question's while its figures load, and a
               * column narrows to its own client: drawn now, it would read one client's
               * cards under a filter that names another.
               */
              <Empty>Reading the board…</Empty>
            ) : (
              <TaskBoard
                columns={data.columns}
                filters={data.filters}
                others={data.summary?.others ?? null}
                serviceName={data.serviceName}
                onShowList={() => {
                  setSearch({ view: 'list' })
                  rememberView('list')
                }}
                at={at}
              />
            )
          ) : (
            <TaskList
              pages={list}
              matching={
                /* Only the new question's count beside the new question's rows. */
                data.summary === undefined || data.isStale
                  ? undefined
                  : { count: data.summary.matching, capped: data.summary.matching_capped }
              }
              clientName={clientOf}
              at={at}
              sort={search.sort}
              dir={search.dir}
              onSort={(column) => {
                const next = nextSort(search.sort, search.dir, column)
                setSearch({ sort: next.sort, dir: next.dir }, HERE)
              }}
              selected={selected}
              onSelected={setSelected}
            />
          )}

          {/*
            The strip at the bottom of the window: what just happened, and what is
            selected. Last in the page's own order, which is what lets it be sticky
            rather than fixed — it covers nothing at the end of the page.
          */}
          <TaskDock>
            <BulkBar
              tasks={list.rows.filter((task) => selected.has(task.ref))}
              matching={data.summary?.matching}
              onClear={() => {
                setSelected(new Set())
              }}
              onFinished={(failed) => {
                setSelected(new Set(failed))
              }}
            />
          </TaskDock>
        </>
      )}
    </TaskNoticeProvider>
  )
}
